import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

import { groupMenuItems } from "@/components/groups/groupMenu";
import { useGroups } from "@/components/groups/useGroups";
import { BottomSheet } from "@/components/shell/BottomSheet";
import { SplitPane } from "@/components/shell/SplitPane";
import { useDialogs } from "@/components/shell/Dialogs";
import { useMenu } from "@/components/shell/Menu";
import type { MenuItemSpec } from "@/components/shell/menuSpec";
import { hasSelectionIn, shouldOpenContextMenu } from "@/lib/contextMenu";
import { nidClause } from "@/lib/groups";
import { isTextEntry } from "@/lib/dom";
import { useCollectionChanged } from "@/lib/useCollectionChanged";
import { type CardsUrlState } from "@/lib/navUrl";
import { TopBar } from "@/components/shell/TopBar";
import { forgetSuggestedTags, parseTagSpec } from "@/lib/tags";
import {
  addTags,
  browseCards,
  deleteNotes,
  listDecks,
  removeTags,
  repositionCards,
  rescheduleCards,
  setDueDate,
  setFlag,
  setSuspended,
  type BrowseRow,
  type DeckSummary,
} from "@/lib/cards";

import { CardRow } from "./CardRow";
import { contextTarget } from "./contextTarget";
import { NoteDock } from "./NoteDock";
import {
  CARD_STATES,
  FLAGS,
  NO_FLAG,
  STATE_LABEL,
  buildQuery,
  flagName,
  isUnfiltered,
  toggle,
  type CardState,
} from "./query";

/**
 * Cards — `5a`. Replaces Anki's browser.
 *
 * **Deck navigation and filtering are different controls.** The deck segmented control in the top
 * bar says where you are; the chips below say what you are asking of it. They compile to one Anki
 * search string (see `query.ts`), which is also what the free-text field speaks.
 */
/**
 * A group's page (日本-601) is THIS surface, scoped — never a second card list.
 *
 * Alex asked for everything 単語 offers inside a group, edit dock and phone sheet included, and the
 * rules this file has accumulated (the dock restore, `contextTarget`, the identity-following reload)
 * are exactly what a copy would drift away from. A scope adds an `nid:` clause, hides the deck
 * control (a group spans decks), replaces the top bar, and adds このグループから外す.
 */
export interface CardsScope {
  /** The group's live notes. Empty means an empty group, never "no filter". */
  noteIds: readonly number[];
  /** Replaces 単語's top bar — the group's name, its drill and its management. */
  topBar: React.ReactNode;
  onRemove: (noteIds: number[]) => void;
}

export function Cards({
  isPhone = false,
  filters,
  onFiltersChange,
  navEpoch = 0,
  footer,
  scope,
}: {
  isPhone?: boolean;
  scope?: CardsScope;
  /**
   * The phone tab bar, rendered UNDER the sheet and never covered by it.
   *
   * A prop rather than a sibling in `App`, and that is `BottomSheet`'s contract rather than a
   * preference: a sheet dragged to full height would otherwise bury its own grip, so the bar has to
   * be inside the sheet's container and below it. Read already passes it for exactly this reason.
   */
  footer?: React.ReactNode;
  /** Held by `App` so they outlive this surface and there is one writer of the URL. */
  filters: CardsUrlState;
  /**
   * `App`'s own setter, so both a value and an updater work and no adapter is needed.
   *
   * `replace` forces a write onto the current history entry instead of a new one. Only the failed
   * restore in `applyPendingNote` needs it — everything else lets `classify` decide, which is what
   * makes the open dock a place you can go Back to and the chips beside it not.
   */
  onFiltersChange: (
    update: SetStateAction<CardsUrlState>,
    opts?: { replace?: boolean },
  ) => void;
  /** Bumped by every Back or Forward — see `pendingNote`, which re-arms on it. */
  navEpoch?: number;
}) {
  const [decks, setDecks] = useState<DeckSummary[]>([]);
  const [rows, setRows] = useState<BrowseRow[]>([]);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState(0);
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [reloadToken, setReloadToken] = useState(0);
  const dialogs = useDialogs();
  const menu = useMenu();

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  /**
   * Put the dock on the note an arming asked for.
   *
   * **Matched by note id, not by index**: row order is the server's and is not stable across a
   * reload. First match wins when a note has several cards — the dock is note-level, so any of its
   * rows shows the right thing.
   *
   * **Any landing on row 0 corrects the entry in place**, and that is `found <= 0` rather than
   * `found < 0`. Row 0 is reported as `note: null` (see `openNote` below), so an entry naming a
   * note that turns out to *be* row 0 is in exactly the same position as one naming a note that is
   * not in the list at all: the reporter would see a focus change and push. Back would then return
   * to that entry, restore row 0, and push again — a Back that appears to do nothing while the
   * stack grows, and one that can never be got past.
   */
  const applyPendingNote = useCallback(
    (rowsNow: readonly BrowseRow[], want: { note: number | null }) => {
      const found = want.note === null ? -1 : rowsNow.findIndex((r) => r.noteId === want.note);
      setFocused(found >= 0 ? found : 0);
      if (found <= 0 && want.note !== null) {
        onFiltersChange((f) => (f.note === null ? f : { ...f, note: null }), { replace: true });
      }
    },
    [onFiltersChange],
  );

  // A job reorders the new queue, writes tags and rewrites fields — all of which this list is
  // showing. `reload` and not a fresh query, so the dock does not jump to row 0 and the checked
  // rows survive: the browse effect already tells "new question" from "fresh data".
  useCollectionChanged(reload);

  useEffect(() => {
    // A group spans decks, so there is no deck control to fill and no landing deck to seed.
    if (scope) return;
    let cancelled = false;
    void listDecks()
      .then(({ decks: found, default: landing }) => {
        if (cancelled) return;
        setDecks(found);
        // Land on the vocab deck rather than on nothing: an unfiltered "All" returns no rows by
        // design, and an empty list on arrival reads as breakage.
        //
        // **Which deck that is comes from the server, and matching a name here is the bug it
        // replaces.** This read `found.find((d) => /vocab/i.test(d.name))` — a guess at the
        // configured deck's spelling, which the client cannot see. Renaming `Japanese::Vocab` to
        // `日本語::単語` matched nothing, so the fallback took over and seeded whatever sorted
        // first: `Default`, an empty deck, with nothing on screen saying why the browse was empty.
        //
        // `undefined` and not `null`: this fires only when NO deck has ever been chosen. `null` is
        // "All, on purpose" — which is both what a refresh on the All segment restores and what the
        // `nid:` deep link asks for, and seeding over either of them is how the card you asked for
        // vanishes behind a deck filter.
        onFiltersChange((current) =>
          current.deck === undefined && found.length > 0
            ? { ...current, deck: landing }
            : current,
        );
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, []);

  // The browse speaks `CardFilters`, where "no deck" has one meaning; the URL needs two. The
  // difference matters only up to the point the deck is decided, which is the effect above.
  const scopeKey = scope ? [...scope.noteIds].join(",") : null;
  const query = useMemo(() => {
    if (scopeKey === null) return buildQuery({ ...filters, deck: filters.deck ?? null });
    if (!scopeKey) return "";
    const rest = buildQuery({ ...filters, deck: null });
    const nid = nidClause(scopeKey.split(",").map(Number));
    return rest ? `${nid} AND ${rest}` : nid;
  }, [filters, scopeKey]);

  /** The query the current rows answer — so a reload can tell "new question" from "fresh data". */
  const answeredQuery = useRef<string | null>(null);

  /**
   * A note to put the dock back on: from the URL at mount, and again after every Back or Forward.
   *
   * Consumed exactly once per arming and must not survive into the next query — having restored
   * your card, typing a new search should land on that search's first row rather than keep hunting
   * for a note you have moved on from. `null` here means "nothing pending"; a pending `{note: null}`
   * means row 0, which is what Back out of an open dock asks for.
   *
   * Re-armed on `navEpoch` because this surface stays mounted across a pop: Back to a different
   * card changes `filters.note` without changing the query, so nothing would re-read it otherwise.
   */
  const pendingNote = useRef<{ note: number | null } | null>(
    filters.note !== null ? { note: filters.note } : null,
  );
  const seenEpoch = useRef(navEpoch);
  if (seenEpoch.current !== navEpoch) {
    seenEpoch.current = navEpoch;
    pendingNote.current = { note: filters.note };
  }

  /**
   * What the dock is currently on, by identity rather than by position.
   *
   * The browse effect's closure cannot see `focused` — its deps are the query and the reload token
   * — so this is how a landing response knows which row to follow rather than which slot.
   */
  const focusedIdentity = useRef<{ noteId: number; cardId: number } | null>(null);

  // Debounced, because the free-text field re-queries on every keystroke and a browse is a
  // collection read.
  useEffect(() => {
    if (!query) {
      setRows([]);
      setTotal(0);
      setTruncated(false);
      // Emptying the query mid-flight used to strand `loading` true: "…" forever and the
      // pick-a-deck empty state suppressed.
      setLoading(false);
      answeredQuery.current = query;
      return;
    }
    let cancelled = false;
    setLoading(true);
    const handle = window.setTimeout(() => {
      void browseCards(query)
        .then((result) => {
          if (cancelled) return;
          setRows(result.rows);
          setTotal(result.total);
          setTruncated(result.truncated);
          if (pendingNote.current !== null) {
            // Restoring the dock you refreshed on, or the one Back is taking you to. Checked FIRST,
            // because the very first browse is always "a genuinely new question" and the branch
            // below would send it to row 0.
            const want = pendingNote.current;
            // Consumed whether or not it matched: a note that was deleted, or rescheduled out of
            // these filters, falls back to the top in silence. You refreshed to get fresh data, not
            // to be told what moved.
            pendingNote.current = null;
            applyPendingNote(result.rows, want);
            setSelected(new Set());
          } else if (answeredQuery.current !== query) {
            // A genuinely new question starts at the top with nothing checked.
            setFocused(0);
            setSelected(new Set());
            // And it says so HERE, with a replace. Returning to row 0 clears `note` out of the
            // URL, and letting the reporter below carry that would make it an ordinary focus
            // change — a push. Typing into the free-text field would then leave a history entry
            // behind it, which is exactly what `classify` says a refinement must never do.
            onFiltersChange((f) => (f.note === null ? f : { ...f, note: null }), { replace: true });
          } else {
            // A reload after a save, a bulk action or a finished job must NOT jump the dock to row
            // 0 — the user may still be typing into the focused note.
            //
            // Followed by identity, not by index. A reload can change which rows are in the list
            // and in what order, so holding the index onto a shifted list silently swaps the note
            // under an open dock: `NoteDock` remounts on whatever now sits at that position and the
            // unsaved draft goes with it. `Priority cleanup` finishing while you have `tag:Prio`
            // typed removes rows above you and does exactly that — which is reachable now that a
            // job can trigger this reload, and was not when only your own writes could.
            //
            // Card first, then note: the row is a card, but a card the reload dropped may still
            // have its note present under a different one. Only when neither survives does the
            // index clamp decide, which is the honest answer to "the thing you were on is gone".
            setFocused((i) => {
              const want = focusedIdentity.current;
              if (want) {
                const byCard = result.rows.findIndex((r) => r.cardId === want.cardId);
                if (byCard >= 0) return byCard;
                const byNote = result.rows.findIndex((r) => r.noteId === want.noteId);
                if (byNote >= 0) return byNote;
              }
              return Math.min(i, Math.max(result.rows.length - 1, 0));
            });
            setSelected((current) => {
              const alive = new Set(result.rows.map((r) => r.cardId));
              return new Set([...current].filter((id) => alive.has(id)));
            });
          }
          answeredQuery.current = query;
          setError(null);
        })
        .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
        .finally(() => !cancelled && setLoading(false));
    }, 140);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query, reloadToken]);

  /**
   * The other half of the restore: the pop that changes nothing but which card you are on.
   *
   * `note` is not part of the query, so Back between two cards in one search re-runs no browse and
   * the branch inside it never fires. Gated on rows being present so it cannot spend the arming
   * before the first browse has landed — that one is the browse's to answer.
   *
   * And gated on those rows ANSWERING THE CURRENT QUERY, for the same reason. A pop that moves the
   * filters as well as the note re-runs the browse, but this effect runs in the same commit —
   * synchronously, while the fetch is still in flight — so without the guard it spends the arming
   * against the previous query's rows, focuses a row from a list about to be replaced, and leaves
   * the landing response with nothing to restore.
   */
  useEffect(() => {
    const want = pendingNote.current;
    if (!want || rows.length === 0 || answeredQuery.current !== query) return;
    pendingNote.current = null;
    applyPendingNote(rows, want);
  }, [navEpoch, rows, query, applyPendingNote]);

  const focusedRow = rows[focused] ?? null;
  focusedIdentity.current = focusedRow
    ? { noteId: focusedRow.noteId, cardId: focusedRow.cardId }
    : null;

  // Keep the URL pointing at the open dock. `null` for the first row, so a fresh arrival's address
  // bar stays clean — and losslessly, since a reload with no `note` starts on row 0 anyway.
  //
  // Identity-guarded, because this fires from an effect: returning the same object when the note
  // has not moved is what stops the write from re-rendering `App` and coming straight back.
  //
  // Debounced, and that is about render cost rather than the URL. The filters live in `App`, so
  // reporting a move costs a round trip back down through this whole list — and `CardRow` is not
  // memoised, so holding `J` through a 500-row deck would reconcile every row twice per keypress.
  // Waiting for the cursor to settle makes that one extra pass per pause instead of one per press.
  const openNote = focused === 0 ? null : (focusedRow?.noteId ?? null);
  useEffect(() => {
    // Nothing is reported until the note the URL arrived with has been consumed. On mount `focused`
    // is 0, so this would otherwise report `null` and clear `note` out of the URL before the first
    // browse had a chance to restore it — and if that browse then failed (hub down), the card would
    // be gone from the address bar for good.
    if (pendingNote.current !== null) return;
    const handle = window.setTimeout(() => {
      onFiltersChange((f) => (f.note === openNote ? f : { ...f, note: openNote }));
    }, 150);
    return () => window.clearTimeout(handle);
  }, [openNote, onFiltersChange]);
  /** A bulk action applies to the checked rows, or to the focused one when nothing is checked. */
  const targetRows = selected.size > 0 ? rows.filter((r) => selected.has(r.cardId)) : focusedRow ? [focusedRow] : [];
  const targetCardIds = targetRows.map((r) => r.cardId);
  const targetNoteIds = [...new Set(targetRows.map((r) => r.noteId))];

  // Group membership for every note on screen: the submenus' ticks and the dock's グループ row
  // both read it, and one request for the list beats one per right-click.
  const groupState = useGroups(rows.map((r) => r.noteId));
  const groupItems = (noteIds: number[]) =>
    groupMenuItems(groupState.groups, noteIds, groupState.membership, groupState.actions);

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      try {
        await action();
        setError(null);
        reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [reload],
  );

  /**
   * The five actions below all take their ids explicitly, and the parameter is **required**.
   *
   * It was optional (`ids = targetCardIds`) for exactly one release and that is a trap rather than
   * a convenience: every one of these is also wired straight to a `<button onClick={…}>` in
   * `BulkBar` and `NoteDock`, and React hands a click handler its `MouseEvent` — so the default
   * never fired and `ids` was the event. `Delete undefined notes?` and a POST carrying a synthetic
   * event instead of an id array. It type-checks, because `(ids?: number[]) => Promise<void>` is
   * assignable to `() => void`; making the parameter required is what makes the compiler catch a
   * bare pass-through, so the call sites wrap.
   */
  const promptDueDate = async (ids: number[]) => {
    // The syntax finally has somewhere to live that is not a sentence inside a system alert.
    const spec = await dialogs.prompt({
      title: `期日を設定${cardCountSuffix(ids.length)}`,
      label: "何日後にしますか？",
      // The spec characters are Anki's own and stay: `0`, `5`, `3-7`, `5!` are what you type.
      hint: "`0` 今日 · `5` · `3-7` 範囲 · `5!` 間隔も設定",
      initial: "0",
      submitLabel: "期日を設定",
    });
    if (spec) void run(() => setDueDate(ids, spec));
  };
  const promptReposition = async (ids: number[]) => {
    // `allowEmpty`, because clearing the field has always meant position 0 here — unlike the due
    // date above, where clearing it cancels. The two disagreed silently before `promptValue`.
    const start = await dialogs.prompt({
      title: `並べ替え${cardCountSuffix(ids.length)}`,
      label: "新しいキュー位置",
      initial: "0",
      allowEmpty: true,
      submitLabel: "並べ替え",
    });
    if (start !== null) void run(() => repositionCards(ids, Number(start) || 0));
  };
  const confirmReschedule = async (ids: number[]) => {
    // Default tone, so Enter agrees with it: the review history is kept, so nothing is lost.
    const ok = await dialogs.confirm({
      title: `${ids.length}枚を再スケジュール？`,
      body: "復習履歴は残ります。",
      confirmLabel: "再スケジュール",
    });
    if (ok) void run(() => rescheduleCards(ids));
  };
  /**
   * One prompt for both directions — `foo -Enrich01` adds `foo` and strips `Enrich01`.
   *
   * A prompt carrying its own syntax is how Reposition and Set due date already work, and the
   * design asks for a single `タグ…` rather than two buttons. `parseTagSpec` is the pure half and
   * is tested; this only decides which of the two endpoints get called.
   *
   * The bulk endpoints are the right tool *here* and the wrong one inside the dock: `add`/`remove`
   * are set semantics across many notes, which is exactly this, whereas a single note's chips need
   * the whole list back to repaint from.
   */
  const promptTags = async (ids: number[]) => {
    const spec = await dialogs.prompt({
      title: `${ids.length}ノートのタグ`,
      label: "タグを追加・削除",
      // The `-` prefix is the syntax `parseTagSpec` reads and stays a literal.
      hint: "スペース区切り。タグの前に - を付けると削除します。",
      submitLabel: "適用",
    });
    if (!spec?.trim()) return;
    const { add, remove } = parseTagSpec(spec);
    if (!add.length && !remove.length) return;
    // `finally`, because this is the first bulk action that makes TWO writes and so the first for
    // which partial success is reachable: a remove that fails after the add landed still changed
    // the collection, and `run`'s catch skips its own `reload()`. The throw still reaches `run`, so
    // the error is reported either way.
    //
    // Only the suggestion cache is dropped here. **The refresh itself is 日本-283's job now** —
    // `cards.ts` deliberately does not send `X-Client-Id` (see `clientId.ts`), so the server's
    // `collectionChanged` frame reads as somebody else's and this tab acts on it like any other.
    // That covers the partial-failure case too, because the add that succeeded published one.
    void run(async () => {
      try {
        if (add.length) await addTags(ids, add.join(" "));
        if (remove.length) await removeTags(ids, remove.join(" "));
      } finally {
        forgetSuggestedTags();
      }
    });
  };
  const confirmDelete = async (ids: number[]) => {
    // Hard delete, no trash (PRD decision 3) — so the confirmation is the only thing between you
    // and permanent loss, and it names the count.
    //
    // `danger` is doing real work here beyond the colour: `defaultFocus` puts the caret on Cancel
    // for a danger confirm, so Enter — pressed by someone who has not read the box — cannot be
    // what empties the collection.
    const ok = await dialogs.confirm({
      title: `${ids.length}ノートを削除？`,
      body: "取り消せません。ゴミ箱はありません。",
      confirmLabel: "削除",
      tone: "danger",
    });
    if (ok) void run(() => deleteNotes(ids));
  };

  /**
   * A card row's right-click (日本-318).
   *
   * Built only from what `BulkBar` already offers, and it acts on `contextTarget`'s answer rather
   * than on `targetRows` — because a pointer introduces the case the "checked rows, or the focused
   * one" rule has no answer for. Right-clicking a row *outside* the selection drops the selection
   * and takes that row, so `14ノートを削除` can never fire on a row you were pointing away from.
   * Cards' delete is a hard delete with no trash, so that is correctness rather than manners.
   */
  const openRowMenu = (event: React.MouseEvent, row: BrowseRow, index: number) => {
    if (
      !shouldOpenContextMenu(event.nativeEvent, {
        selectionInTarget: hasSelectionIn(event.currentTarget),
      })
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    const { rows: targeted, clearSelection } = contextTarget(selected, row.cardId);
    if (clearSelection) setSelected(new Set());
    setFocused(index);

    const cardIds = rows.filter((r) => targeted.includes(r.cardId)).map((r) => r.cardId);
    const noteIds = [...new Set(rows.filter((r) => targeted.includes(r.cardId)).map((r) => r.noteId))];
    const n = cardIds.length;
    const suffix = n === 1 ? "" : ` (${n})`;

    menu.openAt({ x: event.clientX, y: event.clientY }, [
      { kind: "item", id: "due", label: `期日を設定…${suffix}`, onSelect: () => void promptDueDate(cardIds) },
      { kind: "item", id: "pos", label: `並べ替え…${suffix}`, onSelect: () => void promptReposition(cardIds) },
      { kind: "item", id: "reset", label: `再スケジュール${suffix}`, onSelect: () => void confirmReschedule(cardIds) },
      { kind: "item", id: "tags", label: `タグ…`, onSelect: () => void promptTags(noteIds) },
      { kind: "separator", id: "sep1" },
      { kind: "item", id: "suspend", label: `停止${suffix}`, onSelect: () => void run(() => setSuspended(cardIds, true)) },
      { kind: "item", id: "unsuspend", label: `停止解除${suffix}`, onSelect: () => void run(() => setSuspended(cardIds, false)) },
      {
        kind: "submenu",
        id: "flag",
        label: "フラグ",
        items: [
          ...FLAGS.map((flag): MenuItemSpec => ({
            kind: "item",
            id: `flag-${flag.value}`,
            label: flag.label,
            swatch: `var(${flag.token})`,
            onSelect: () => void run(() => setFlag(cardIds, flag.value)),
          })),
          { kind: "item", id: "flag-0", label: NO_FLAG.label, onSelect: () => void run(() => setFlag(cardIds, 0)) },
        ],
      },
      { kind: "separator", id: "sep-group" },
      { kind: "submenu", id: "groups", label: "グループに追加", items: groupItems(noteIds) },
      ...(scope
        ? [
            {
              kind: "item",
              id: "group-remove",
              label: "このグループから外す",
              onSelect: () => scope.onRemove(noteIds),
            } as const,
          ]
        : []),
      { kind: "separator", id: "sep2" },
      {
        kind: "item",
        id: "delete",
        label: `${noteIds.length}ノートを削除`,
        danger: true,
        onSelect: () => void confirmDelete(noteIds),
      },
    ]);
  };

  // J/K move, and the shortcuts the dock header advertises.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // `J` in the dock's English field does not merely swallow the keystroke — it moves `focused`,
      // which remounts the dock on another note and drops the unsaved draft. The dock's paste
      // handler asks the same question, so the guard lives in `lib/dom.ts` and is tested there.
      if (isTextEntry(event.target)) return;
      // Bare keys only. Every browser reserves some modified letter — Ctrl+K is the address bar's
      // on Firefox — and a list that moved its focus on the way out of the surface was noticed the
      // one time this app briefly bound that chord. `dictionaryKeys.ts` refuses a modified key for
      // the same reason.
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key === "j" || event.key === "ArrowDown") {
        event.preventDefault();
        setFocused((i) => Math.min(i + 1, Math.max(rows.length - 1, 0)));
      } else if (event.key === "k" || event.key === "ArrowUp") {
        event.preventDefault();
        setFocused((i) => Math.max(i - 1, 0));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rows.length]);

  const list = (
    <div className="flex h-full flex-col">
      <FilterBar
        filters={filters}
        onChange={onFiltersChange}
        // **The deck control on a phone, because the top bar cannot carry it.** `TopBar`'s phone
        // form draws the destination word and up to two 44px targets and renders no `children` at
        // all — so the `DeckControl` passed below is simply absent at 390px, and the deck scope
        // became unreachable rather than relocated. This is where the comment on that `TopBar`
        // already said it went.
        decks={isPhone && !scope ? decks : undefined}
        activeDeck={filters.deck ?? null}
        onDeckChange={(deck) => onFiltersChange((current) => ({ ...current, deck }))}
      />

      {selected.size > 0 ? (
        <BulkBar
          count={selected.size}
          onClear={() => setSelected(new Set())}
          onDueDate={() => void promptDueDate(targetCardIds)}
          onReposition={() => void promptReposition(targetCardIds)}
          onReschedule={() => void confirmReschedule(targetCardIds)}
          onTags={() => void promptTags(targetNoteIds)}
          onSuspend={() => void run(() => setSuspended(targetCardIds, true))}
          onUnsuspend={() => void run(() => setSuspended(targetCardIds, false))}
          onFlag={(flag) => void run(() => setFlag(targetCardIds, flag))}
          onDelete={() => void confirmDelete(targetNoteIds)}
          onGroups={(trigger) => menu.openFrom(trigger, groupItems(targetNoteIds), "cards.bulk.groups")}
          onRemoveFromGroup={scope ? () => scope.onRemove(targetNoteIds) : undefined}
        />
      ) : (
        <div className="flex shrink-0 items-center gap-2 border-b border-line px-[18px] py-2">
          <span className="text-meta text-ink-3">
            {loading
              ? "…"
              : `${total.toLocaleString()}枚`}
            {truncated && ` · 先頭${rows.length.toLocaleString()}枚を表示`}
          </span>
          <span className="flex-1" />
          {/* `J/Kで移動` names keys a phone does not have. The app's rule is that a shortcut
              appears beside the control it triggers, not that it always appears. */}
          {!isPhone && <span className="font-mono text-meta text-ink-4">J/Kで移動</span>}
        </div>
      )}

      {error && (
        <p className="shrink-0 border-b border-accent-line bg-accent-tint px-[18px] py-2 text-secondary text-accent-hi">
          {error}
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        {rows.length === 0 && !loading && (
          <p className="px-[18px] py-4 text-secondary text-ink-3">
            {scope && scope.noteIds.length === 0
              ? "このグループは空です。"
              : scope && isUnfiltered(filters)
                ? "このグループのカードは見つかりませんでした。"
                : !query
              ? "デッキを選ぶか、フィルタを入力してください。"
              : isUnfiltered(filters)
                ? "このデッキは空です。"
                : "この条件に一致するものはありません。"}
          </p>
        )}
        {rows.map((row, index) => (
          <CardRow
            key={row.cardId}
            row={row}
            selected={selected.has(row.cardId)}
            focused={index === focused}
            onFocus={() => setFocused(index)}
            isPhone={isPhone}
            onContextMenu={(event) => openRowMenu(event, row, index)}
            onToggleSelect={() =>
              setSelected((current) => {
                const next = new Set(current);
                if (next.has(row.cardId)) next.delete(row.cardId);
                else next.add(row.cardId);
                return next;
              })
            }
          />
        ))}
      </div>
    </div>
  );

  const dock = focusedRow ? (
    <NoteDock
      row={focusedRow}
      groups={groupState}
      onSaved={reload}
      onDelete={() => void confirmDelete(targetNoteIds)}
      onSetDueDate={() => void promptDueDate(targetCardIds)}
      onReposition={() => void promptReposition(targetCardIds)}
      onReschedule={() => void confirmReschedule(targetCardIds)}
    />
  ) : (
    <p className="px-[22px] py-4 text-secondary text-ink-3">カードを選ぶとノートが表示されます。</p>
  );

  return (
    <>
      {/* The deck control is desktop furniture and stays there: the segmented control lists every
          deck with a count, which does not fit 390px. On a phone the deck moves into the filter bar
          below, which already scrolls.

          **The `Ctrl+K` hint that used to sit beside it is gone** (Alex, 27 Aug 2026). It named a
          key nothing handled from the redesign until 日本-497 wired it onto 検索, and that shortcut
          is now removed too — so the label went with it rather than going back to being furniture
          for a key that does nothing. The design canvas still draws it; this is a deliberate
          departure. */}
      {scope ? (
        scope.topBar
      ) : (
        <TopBar title="単語" destinationId="cards">
          <DeckControl
            decks={decks}
            active={filters.deck ?? null}
            onSelect={(deck) => onFiltersChange((f) => ({ ...f, deck }))}
          />
        </TopBar>
      )}
      {/* **The note is the same SHEET Read's dock is** — the Mobile canvas says so directly: "the
          note is the same sheet as 読書's dock". It was a `SplitPane` with `axis="stacked"`, which
          is a split turned on its side rather than a sheet: the list keeps a fixed share of the
          screen, the note keeps another, and neither can be got out of the way. A sheet rests at
          ~40%, drags to full height and **swipes away to leave the list clean**, which is the
          gesture a phone actually has.

          One component, three consumers, and that matters beyond consistency: it is the same grip
          in the same place with the same three positions, and the position is persisted per
          surface — so Cards remembers how far you like the note open independently of Read. */}
      {isPhone ? (
        <BottomSheet
          surface={scope ? "groups" : "cards"}
          main={list}
          sheet={dock}
          footer={footer}
          openOn={focusedRow?.noteId}
        />
      ) : (
        <SplitPane surface={scope ? "groups" : "cards"} main={list} dock={dock} defaultDockSize={520} />
      )}
    </>
  );
}

/** Where you are — deliberately not a filter. */
function DeckControl({
  decks,
  active,
  onSelect,
}: {
  decks: DeckSummary[];
  active: string | null;
  onSelect: (deck: string | null) => void;
}) {
  const segments: Array<{ label: string; value: string | null; count: number | null }> = [
    ...decks.map((d) => ({
      // The leaf name: "Japanese::Vocab" is "Vocab" in a control that is already scoped.
      label: d.name.split("::").pop() ?? d.name,
      value: d.name,
      count: d.cardCount,
    })),
    { label: "すべて", value: null, count: null },
  ];

  return (
    <div className="flex items-center gap-[2px] overflow-x-auto rounded-[var(--r-3)] bg-sunken p-[2px]">
      {segments.map((segment) => {
        const isActive = segment.value === active;
        return (
          <button
            // The full deck name: two decks can share a leaf ("A::Vocab", "B::Vocab") and React
            // drops one of a duplicated key. すべて has value null, hence the fallback.
            key={segment.value ?? "__all__"}
            type="button"
            onClick={() => onSelect(segment.value)}
            className={[
              "shrink-0 rounded-[var(--r-2)] px-[10px] py-[5px] text-[14px]/[1] font-semibold",
              isActive ? "bg-panel text-ink shadow-[var(--sh-inset)]" : "text-ink-3",
            ].join(" ")}
          >
            {segment.label}
            {segment.count !== null && (
              <span className="ml-[6px] font-mono text-[14px] opacity-60">
                {segment.count.toLocaleString()}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** What you are asking of it. */
function FilterBar({
  filters,
  onChange,
  decks,
  activeDeck,
  onDeckChange,
}: {
  filters: CardsUrlState;
  onChange: Dispatch<SetStateAction<CardsUrlState>>;
  /** The deck segments, on a phone only — `undefined` on the desktop, where the top bar has them. */
  decks?: DeckSummary[];
  activeDeck: string | null;
  onDeckChange: (deck: string | null) => void;
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line px-[18px] py-[10px]">
      {decks && (
        <DeckControl decks={decks} active={activeDeck} onSelect={onDeckChange} />
      )}
      {CARD_STATES.map((state) => (
        <Chip
          key={state}
          check={`cards.chip.${state}`}
          active={filters.states.includes(state)}
          onClick={() =>
            onChange({ ...filters, states: toggle(filters.states, state) as CardState[] })
          }
        >
          {STATE_LABEL[state]}
        </Chip>
      ))}

      <span className="mx-1 h-4 w-px bg-line" />

      {FLAGS.map((flag) => {
        const active = filters.flags.includes(flag.value);
        return (
          <button
            key={flag.value}
            type="button"
            aria-label={flagName(flag.value) ?? undefined}
            aria-pressed={active}
            onClick={() => onChange({ ...filters, flags: toggle(filters.flags, flag.value) })}
            className="size-[18px] shrink-0 rounded-[var(--r-2)]"
            style={{
              background: `var(${flag.token})`,
              outline: active ? "2px solid var(--ink)" : undefined,
              outlineOffset: 1,
            }}
          />
        );
      })}

      <input
        value={filters.text}
        onChange={(e) => onChange({ ...filters, text: e.target.value })}
        placeholder="tag:leech -is:suspended"
        aria-label="Anki検索"
        className="ml-auto h-8 min-w-[180px] flex-1 rounded-[var(--r-2)] border border-line-strong bg-void px-[10px] font-mono text-[14px]/[1] text-ink outline-none placeholder:text-ink-4 focus:border-accent"
      />
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
  check,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  /** `checks/` hook. The chip's label is the state name and is about to be Japanese. */
  check?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      data-check={check}
      className={[
        "shrink-0 rounded-[var(--r-2)] border px-[10px] py-[4px] text-[14px]/[1.4] font-semibold",
        active
          ? "border-accent bg-accent-tint text-accent-text"
          : "border-line-strong bg-void text-ink-2",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

/** Replaces the list header when rows are checked. */
function BulkBar({
  count,
  onClear,
  onDueDate,
  onReposition,
  onReschedule,
  onTags,
  onSuspend,
  onUnsuspend,
  onFlag,
  onDelete,
  onGroups,
  onRemoveFromGroup,
}: {
  count: number;
  onClear: () => void;
  onDueDate: () => void;
  onReposition: () => void;
  onReschedule: () => void;
  onTags: () => void;
  onSuspend: () => void;
  onUnsuspend: () => void;
  onFlag: (flag: number) => void;
  onDelete: () => void;
  /** Opens the グループに追加 menu under the button that asked. */
  onGroups: (trigger: HTMLElement) => void;
  /** Present only inside a group (日本-601). */
  onRemoveFromGroup?: () => void;
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-accent-line bg-accent-tint px-[18px] py-2">
      <span className="text-secondary font-semibold text-accent-text">{count}件選択</span>
      <button type="button" onClick={onClear} className="text-meta text-ink-3 underline">
        解除
      </button>
      <span className="mx-1 h-4 w-px bg-accent-border" />
      <BulkAction onClick={onDueDate}>期日を設定</BulkAction>
      <BulkAction onClick={onReposition}>並べ替え</BulkAction>
      <BulkAction onClick={onReschedule}>再スケジュール</BulkAction>
      {/* After 再スケジュール, per the handoff's bulk-bar order. */}
      <BulkAction onClick={onTags}>タグ…</BulkAction>
      <BulkAction onClick={onSuspend}>停止</BulkAction>
      <BulkAction onClick={onUnsuspend}>停止解除</BulkAction>
      <BulkAction onClick={(event) => onGroups(event.currentTarget)}>グループ…</BulkAction>
      {onRemoveFromGroup && (
        <BulkAction onClick={onRemoveFromGroup}>このグループから外す</BulkAction>
      )}
      <span className="mx-1 h-4 w-px bg-accent-border" />
      {[...FLAGS, NO_FLAG].map((flag) => (
        <button
          key={flag.value}
          type="button"
          aria-label={flagName(flag.value) ?? undefined}
          onClick={() => onFlag(flag.value)}
          className="size-[16px] shrink-0 rounded-[var(--r-2)] border border-line-strong"
          style={{ background: `var(${flag.token})` }}
        />
      ))}
      <span className="flex-1" />
      <button
        type="button"
        onClick={onDelete}
        className="rounded-[var(--r-2)] border border-accent-line bg-accent-tint px-[11px] py-[6px] text-[14px]/[1] font-semibold text-accent-hi"
      >
        削除
      </button>
    </div>
  );
}

function BulkAction({
  onClick,
  children,
}: {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[var(--r-2)] border border-line-strong bg-panel px-[10px] py-[6px] text-[14px]/[1] font-semibold text-ink-2"
    >
      {children}
    </button>
  );
}

/**
 * ` · 14枚` for a bulk action, nothing for a single one.
 *
 * The count belongs in the title of a prompt because the prompt has no other way to say what it is
 * about to touch — a confirm names it in the question, but `期日を設定` alone would be silent
 * about whether it means the row you are on or the fourteen you ticked.
 */
function cardCountSuffix(count: number): string {
  return count === 1 ? "" : ` · ${count}枚`;
}
